import React, { useMemo, useState, useEffect } from "react";

import {
  TextField,
  InputAdornment,
  Box,
  Paper,
  List,
  ListItem,
  ListItemButton,
  Divider,
  Stack,
  Chip,
  Typography,
  ClickAwayListener,
  CircularProgress,
} from "@mui/material";
import { SearchOutlined } from "@mui/icons-material";
import { useCustom } from "@refinedev/core";
import { useMarketplaceStore } from "../../../store/useMarketplaceStore";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../../hooks/useTranslation";
import { OrderOptimizedListResponse } from "../../../pages/orders/utils/types";
import { getRuntimeEnv } from "../../../providers/getRuntimeEnv";
import { Copyable } from "../../copyable/Copyable";

/* =========================
   HELPERS
========================= */
const InfoRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "160px 1fr",
      alignItems: "center",
      gap: 1,
    }}
  >
    <Typography variant="caption" fontWeight={500}>
      {label}:
    </Typography>

    <Box>{children}</Box>
  </Box>
);

const ASIN_REGEX = /^[A-Z0-9]{10}$/i;
const isAsin = (value: string) => ASIN_REGEX.test(value);

export const OrderSearchBar: React.FC = () => {
  const { selectedCountry } = useMarketplaceStore();
  const navigate = useNavigate();
  const { translateWithoutPrefix } = useTranslation("");
  const inputRef = React.useRef<HTMLDivElement | null>(null);

  const [value, setValue] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [maxHeight, setMaxHeight] = useState(window.innerHeight);

  const [matchedOrders, setMatchedOrders] = useState<
    OrderOptimizedListResponse[]
  >([]);
  const [openResults, setOpenResults] = useState(false);

  const runtimeEnv = useMemo(() => getRuntimeEnv(), []);
  const isDevEnv = runtimeEnv === "dev";

  const trimmedValue = value.trim();

  /* =========================
     DEBOUNCE
  ========================= */
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedValue(trimmedValue);
    }, 2000);

    return () => clearTimeout(timeout);
  }, [trimmedValue]);

  /* =========================
     QUERY
  ========================= */
  const queries = useMemo(() => {
    if (!debouncedValue) return [];

    const normalizedValue = debouncedValue.replaceAll(/\s/g, "").toUpperCase();

    if (isAsin(debouncedValue)) {
      return [
        {
          field: "predicate",
          value: JSON.stringify({
            type: "and",
            predicates: [
              {
                property: "asin",
                type: "eq",
                value: normalizedValue,
              },
            ],
          }),
        },
      ];
    }

    return [
      {
        field: "predicate",
        value: JSON.stringify({
          type: "or",
          predicates: [
            {
              property: "searchTerm",
              type: "eq",
              value: debouncedValue,
            },
            {
              property: "supplyOrderId",
              type: "eq",
              value: debouncedValue,
            },
          ],
        }),
      },
    ];
  }, [debouncedValue]);

  /* =========================
     FETCH
  ========================= */
  const { refetch, isFetching } = useCustom<OrderOptimizedListResponse[]>({
    url: selectedCountry?.id
      ? `store/${selectedCountry.id}/order/optimized`
      : "",
    method: "get",
    meta: {
      queries,
    },
    queryOptions: {
      enabled: false,
      staleTime: 0,
      onSuccess: (response) => {
        const orders = response.data;
        if (!orders || orders.length === 0) {
          setOpenResults(false);
          return;
        }

        if (orders.length === 1) {
          navigate(`/order-detail/${orders[0].id}`);
          setOpenResults(false);
          return;
        }

        setMatchedOrders(orders);
        setOpenResults(true);
      },
    },
  });

  /* =========================
     AUTO FETCH (DEBOUNCED)
  ========================= */
  useEffect(() => {
    if (!debouncedValue) return;
    if (!selectedCountry?.id) return;

    refetch();
  }, [debouncedValue, selectedCountry?.id]);

  /* =========================
     MANUAL SEARCH (ENTER)
  ========================= */
  const handleSearch = () => {
    if (!selectedCountry?.id) return;
    if (!trimmedValue) return;

    setDebouncedValue(trimmedValue); // debounce bypass
  };

  /* =========================
     DROPDOWN HEIGHT
  ========================= */
  useEffect(() => {
    if (!inputRef.current) return;

    const rect = inputRef.current.getBoundingClientRect();
    const availableHeight = window.innerHeight - rect.bottom;

    setMaxHeight(availableHeight * 0.66);
  }, [openResults]);

  return (
    <ClickAwayListener onClickAway={() => setOpenResults(false)}>
      <Box sx={{ position: "relative", flex: 1 }} ref={inputRef}>
        <TextField
          placeholder={translateWithoutPrefix("common.searchByAny")}
          size="small"
          fullWidth
          autoComplete="off"
          value={value}
          disabled={!selectedCountry?.id || isFetching}
          onChange={(e) => {
            setValue(e.target.value);
            setOpenResults(false);
          }}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            handleSearch();
          }}
          sx={{
            backgroundColor: "background.paper",
            borderRadius: 1,
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  {isFetching ? (
                    <CircularProgress size={18} thickness={5} />
                  ) : (
                    <SearchOutlined />
                  )}
                </InputAdornment>
              ),
            },
          }}
        />

        {openResults && matchedOrders.length > 1 && (
          <Paper
            elevation={10}
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              mt: 0.5,
              zIndex: 10,
              maxHeight,
              overflowY: "auto",
            }}
          >
            <List dense>
              {matchedOrders.map((order) => {
                const image = order.images?.[0];
                const asin = order.orderItems?.[0]?.asin;
                const supplyStatus = order.supplyOrders?.[0]?.supplyOrderStatus;

                const idLabel = translateWithoutPrefix(
                  "pages.orders.list.fields.orderId",
                );
                const amazonLabel = translateWithoutPrefix(
                  "pages.orders.list.fields.amazonOrderId",
                );
                const barcodeLabel = translateWithoutPrefix(
                  "pages.orders.list.fields.barcode",
                );
                const statusLabel = translateWithoutPrefix(
                  "pages.orders.list.fields.status.title",
                );
                const supplyStatusLabel = translateWithoutPrefix(
                  "common.supplyOrderStatus.title",
                );
                const unknownText = translateWithoutPrefix("common.unknown");

                const amazonText = order.amazonOrderId ?? unknownText;
                const barcodeText = order.barcode ?? "-";

                const statusText =
                  translateWithoutPrefix(
                    "pages.orders.list.fields.status." + order.orderStatus,
                  ) ?? "Unknown";

                const supplyStatusText =
                  supplyStatus &&
                  translateWithoutPrefix(
                    "common.supplyOrderStatus." + supplyStatus,
                  );

                const fallbackImage = isDevEnv
                  ? "/icons/logo-dev.svg"
                  : "/icons/logo.svg";
                const imageSrc = image || fallbackImage;

                return (
                  <React.Fragment key={order.id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => {
                          navigate(`/order-detail/${order.id}`);
                          setOpenResults(false);
                        }}
                        sx={{
                          alignItems: "flex-start",
                          gap: 1.5,
                          py: 1.2,
                          borderBottom: "1px solid rgba(255,255,255,0.05)",
                        }}
                      >
                        {/* IMAGE */}
                        <Box
                          component="img"
                          src={imageSrc}
                          alt="order"
                          sx={{
                            width: 56,
                            height: 56,
                            objectFit: "contain",
                            borderRadius: 1.5,
                            flexShrink: 0,
                            backgroundColor: "background.paper",
                          }}
                        />

                        {/* RIGHT SIDE */}
                        <Stack gap={1}>
                          <InfoRow label={idLabel}>
                            <Copyable
                              size="small"
                              initialLabel={order.id.toString()}
                            />
                          </InfoRow>

                          {order.amazonOrderId && (
                            <InfoRow label={amazonLabel}>
                              <Copyable
                                size="small"
                                initialLabel={amazonText}
                              />
                            </InfoRow>
                          )}

                          {order.barcode && (
                            <InfoRow label={barcodeLabel}>
                              <Copyable
                                size="small"
                                initialLabel={barcodeText}
                              />
                            </InfoRow>
                          )}

                          {asin && (
                            <InfoRow label="ASIN">
                              <Copyable
                                size="small"
                                initialLabel={asin.toUpperCase()}
                              />
                            </InfoRow>
                          )}

                          <InfoRow label={statusLabel}>
                            <Chip
                              size="small"
                              label={statusText}
                              sx={{ fontSize: 11 }}
                            />
                          </InfoRow>

                          {supplyStatusText && (
                            <InfoRow label={supplyStatusLabel}>
                              <Chip
                                size="small"
                                label={supplyStatusText}
                                sx={{ fontSize: 11 }}
                              />
                            </InfoRow>
                          )}
                        </Stack>
                      </ListItemButton>
                    </ListItem>
                    <Divider sx={{ borderColor: "primary.main" }} />
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};
